/**
 * Platform detection and keyboard shortcut utilities
 */

/**
 * Detects if the current platform is macOS
 */
export const isMac = (): boolean => {
  return typeof navigator !== 'undefined' && navigator.platform.toLowerCase().includes('mac');
};

/**
 * Gets the appropriate modifier key for the current platform
 * @returns 'CMD' on macOS, 'CTRL' on other platforms
 */
export const getModifierKey = (): string => {
  return isMac() ? 'CMD' : 'CTRL';
};

/**
 * Gets the display symbol for the modifier key
 * @param modifierKey - The modifier key ('CMD' or 'CTRL')
 * @returns The appropriate symbol ('⌘' for CMD, '⌃' for CTRL)
 */
export const getModifierKeySymbol = (modifierKey?: string): string => {
  const key = modifierKey || getModifierKey();
  return key === 'CMD' ? '⌘' : '⌃';
};

/**
 * Gets the display text for any keyboard key, converting modifier keys to symbols
 * @param key - The key name (e.g., 'CMD', 'CTRL', 'ENTER', 'N')
 * @returns The appropriate display symbol for keys
 */
export const getKeyDisplayText = (key: string): string => {
  const upperKey = key.toUpperCase();

  // Map keys to symbols
  const keyMap: Record<string, string> = {
    CMD: '⌘',
    CTRL: '⌃',
    SHIFT: '⇧',
    ALT: '⌥',
    OPTION: '⌥',
    ENTER: '⏎',
    RETURN: '⏎',
  };

  return keyMap[upperKey] || key;
};

/**
 * Platform-aware keyboard shortcut utilities
 */
export const platformUtils = {
  isMac,
  getModifierKey,
  getModifierKeySymbol,
  getKeyDisplayText,
} as const;
